import React, { useEffect, useState } from 'react';
import {
  Box,
  Tooltip,
  Chip,
  Drawer,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  //TablePagination,
  Paper,
  Button,
  TextField,
  // InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Typography, PageHeader } from '@/components/ui';
//import type { PageHeaderAction } from '@/components/ui/PageHeader';
import {
  Visibility as ViewIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Add as AddIcon,
  Close as CloseIcon,
  Remove as RemoveIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useCommonBreadcrumbs } from '@/utils/breadcrumbs';
import { getBreadcrumbConfig } from '@/config/breadcrumbs';
import { formatCurrency } from '@/utils/formatters';
import { format as formatMomentDate } from '@/utils/time';
import { routingRulesService } from '@/services/routingRulesService';
//import RoutingSummaryCards from '../../components/RoutingSummaryCards/RoutingSummaryCards';
import { toast } from 'react-hot-toast';

export const RoutingRule: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [selectedRoutingRules, setSelectedRoutingRules] = useState<any | null>(null);
  const [RoutingRules, setRoutingRules] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState<string>('');

  // const [SettlementearchTerm, setSettlementearchTerm] = useState('');
  // const [merchantSettlementearchTerm, setMerchantSettlementearchTerm] = useState('');
  // const [Settlementtats, setSettlementtats] = useState('');

  // Pagination state
  const [page, setPage] = useState(0);
  //const [rowsPerPage, setRowsPerPage] = useState(50); // Default rows per page

  // Sorting state
  const [sortField, setSortField] = useState<keyof any>('transaction_date_time');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const STATUSES = {
    SUCCESS: 'SUCCESS',
    FAILURE: 'FAILURE',
    DROP: 'DROP',
    CANCELLED: 'CANCELLED',
    INITIATED: 'INITIATED',
  } as const;

  // Add Rule Modal State
  const [isAddRuleModalOpen, setIsAddRuleModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [paymentGateways, setPaymentGateways] = useState<any[]>([]);
  const [isLoadingGateways, setIsLoadingGateways] = useState(false);

  // Delete Rule Modal State
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [ruleToDelete, setRuleToDelete] = useState<any>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Tab state
  const [activeTab, setActiveTab] = useState(0);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    priority: '',
    active: true,
    strategy: '',
    sticky_ttl_sec: '0',
    condition: {
      attribute: '',
      op: '',
      value: '',
      mode: '',
    },
    strategies: [] as Array<{
      payment_gateway: string;
      sequence_order: number;
      batch_size: number;
      duration_seconds: number;
      weight: number;
    }>,
  });

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Strategy options
  const STRATEGY_OPTIONS = [
    { value: 'FIXED', label: 'FIXED' },
    { value: 'SEQUENTIAL', label: 'SEQUENTIAL' },
    { value: 'TIME_SLICE', label: 'TIME SLICE' },
    { value: 'WEIGHTED', label: 'WEIGHTED' },
  ];

  // Operator options
  const OPERATOR_OPTIONS = [
    { value: 'LTE', label: 'LTE (Less Than or Equal)' },
    { value: 'GTE', label: 'GTE (Greater Than or Equal)' },
    { value: 'BETWEEN', label: 'BETWEEN' },
  ];

  // Condition attribute options
  const ATTRIBUTE_OPTIONS = [
    { value: 'amount', label: 'AMOUNT' },
    { value: 'failure', label: 'FAILURE' },
    { value: 'payment_mode', label: 'PAYMENT MODE' },
  ];

  // Payment mode options
  const PAYMENT_MODE_OPTIONS = [
    { value: 'CC', label: 'CREDIT_CARD' },
    { value: 'DC', label: 'DEBIT_CARD' },
    { value: 'UPI', label: 'UPI' },
    { value: 'NB', label: 'NET_BANKING' },
    { value: 'CASH', label: 'WALLETS' },
    { value: 'EMI', label: 'EMI' },
    { value: 'PAY_LATER', label: 'PAY_LATER' },
  ];

  // Load Settlement with server-side pagination
  useEffect(() => {
    loadRoutingRules();
    //loadSettlementtats();
  }, [page, sortField, sortDirection]);

  // Load payment gateways when modal opens
  useEffect(() => {
    if (isAddRuleModalOpen) {
      loadPaymentGateways();
    }
  }, [isAddRuleModalOpen]);

  const loadRoutingRules = async () => {
    setIsLoading(true);
    try {
      const apiResponse = await routingRulesService.getRoutingRules();

      setRoutingRules(apiResponse.results || []);
      setTotalCount(apiResponse.count || 0);
    } catch (error) {
      console.error('Error loading RoutingRules:', error);
      setRoutingRules([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  };

  const loadPaymentGateways = async () => {
    setIsLoadingGateways(true);
    try {
      const response = await routingRulesService.getPaymentGateways();
      setPaymentGateways(response.results || response.data || []);
    } catch (error) {
      console.error('Error loading payment gateways:', error);
      toast.error('Failed to load payment gateways');
      setPaymentGateways([]);
    } finally {
      setIsLoadingGateways(false);
    }
  };

  const handleOpenAddRuleModal = () => {
    setIsAddRuleModalOpen(true);
    // Reset form
    setFormData({
      name: '',
      priority: '',
      active: true,
      strategy: '',
      sticky_ttl_sec: '0',
      condition: {
        attribute: '',
        op: '',
        value: '',
        mode: '',
      },
      strategies: [
        {
          payment_gateway: '',
          sequence_order: 1,
          batch_size: 1,
          duration_seconds: 0,
          weight: 0,
        },
      ],
    });
    setFormErrors({});
  };

  const handleCloseAddRuleModal = () => {
    setIsAddRuleModalOpen(false);
    setFormData({
      name: '',
      priority: '',
      active: true,
      strategy: '',
      sticky_ttl_sec: '0',
      condition: {
        attribute: '',
        op: '',
        value: '',
        mode: '',
      },
      strategies: [
        {
          payment_gateway: '',
          sequence_order: 1,
          batch_size: 1,
          duration_seconds: 0,
          weight: 0,
        },
      ],
    });
    setFormErrors({});
    setStatusFilter('');
  };

  const handleInputChange =
    (field: string) =>
      (event: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
        const value = event.target.value;
        setFormData(prev => ({
          ...prev,
          [field]: value,
        }));
        // Clear error
        if (formErrors[field]) {
          setFormErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[field];
            return newErrors;
          });
        }
      };

  const handleSelectChange = (field: string) => (event: any) => {
    const value = event.target.value;
    setStatusFilter(value);
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    // Clear error
    if (formErrors[field]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleConditionChange =
    (field: string) =>
      (event: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
        const value = event.target.value;
        setFormData(prev => ({
          ...prev,
          condition: {
            ...prev.condition,
            [field]: value,
          },
        }));
        // Clear error
        const errorKey = `condition.${field}`;
        if (formErrors[errorKey]) {
          setFormErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[errorKey];
            return newErrors;
          });
        }
      };

  const handleConditionSelectChange = (field: string) => (event: any) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      condition: {
        ...prev.condition,
        [field]: value,
        // Auto-select GTE when failure is selected
        ...(field === 'attribute' && value === 'failure' ? { op: 'GTE' } : {}),
      },
    }));
    // Clear error
    const errorKey = `condition.${field}`;
    if (formErrors[errorKey]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[errorKey];
        return newErrors;
      });
    }
  };

  const handleActiveChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      active: event.target.checked,
    }));
  };

  const handleApiErrors = (errorData: any) => {
    if (errorData.errors) {
      Object.keys(errorData.errors).forEach(key => {
        const errorMessages = errorData.errors[key];
        if (Array.isArray(errorMessages)) {
          errorMessages.forEach(msg => toast.error(msg));
        } else {
          toast.error(errorMessages);
        }
      });
    } else if (errorData.message) {
      toast.error(errorData.message);
    } else {
      toast.error('An error occurred');
    }
  };

  const handlePaymentGatewayChange = (event: any) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      strategies: typeof value === 'string' ? value.split(',') : value,
    }));
    // Clear error
    if (formErrors.strategies) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.strategies;
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.name.trim()) {
      errors.name = 'Rule name is required';
    }

    if (!formData.priority.trim()) {
      errors.priority = 'Priority is required';
    } else if (isNaN(Number(formData.priority))) {
      errors.priority = 'Priority must be a number';
    }

    if (!formData.strategy) {
      errors.strategy = 'Strategy is required';
    }

    if (!formData.condition.attribute) {
      errors['condition.attribute'] = 'Attribute is required';
    }

    if (formData.condition.attribute === 'payment_mode') {
      if (!formData.condition.mode) {
        errors['condition.mode'] = 'Payment mode is required';
      }
    } else if (formData.condition.attribute) {
      if (!formData.condition.op) {
        errors['condition.op'] = 'Operator is required';
      }
      if (!formData.condition.value.trim()) {
        errors['condition.value'] = 'Value is required';
      }
    }

    if (formData.strategies.length === 0) {
      errors.strategies = 'At least one payment gateway is required';
    } else {
      formData.strategies.forEach((strategy, index) => {
        if (!strategy.payment_gateway) {
          errors[`strategies.${index}.payment_gateway`] = 'Payment gateway is required';
        }

        if (formData.strategy === 'SEQUENTIAL') {
          if (!strategy.sequence_order || strategy.sequence_order < 1) {
            errors[`strategies.${index}.sequence_order`] = 'Sequence is required';
          }
          if (!strategy.batch_size || strategy.batch_size < 1) {
            errors[`strategies.${index}.batch_size`] = 'Batch size is required';
          }
        }

        if (formData.strategy === 'TIME_SLICE') {
          if (!strategy.sequence_order || strategy.sequence_order < 1) {
            errors[`strategies.${index}.sequence_order`] = 'Sequence is required';
          }
          if (!strategy.duration_seconds || strategy.duration_seconds < 1) {
            errors[`strategies.${index}.duration_seconds`] = 'Duration is required';
          }
        }

        if (formData.strategy === 'WEIGHTED') {
          if (!strategy.sequence_order || strategy.sequence_order < 1) {
            errors[`strategies.${index}.sequence_order`] = 'Sequence is required';
          }
          if (!strategy.weight || strategy.weight < 1) {
            errors[`strategies.${index}.weight`] = 'Weight is required';
          }
        }
      });
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        name: formData.name.trim(),
        priority: Number(formData.priority),
        active: formData.active,
        strategy: formData.strategy,
        sticky_ttl_sec: Number(formData.sticky_ttl_sec) || 0,
        condition: formData.condition.attribute === 'payment_mode' ? {
          attribute: 'payment-mode',
          mode: formData.condition.mode,
        } : {
          attribute: formData.condition.attribute,
          op: formData.condition.op,
          value: formData.condition.value.trim(),
        },
        strategies: formData.strategies,
      };

      const response = await routingRulesService.createRoutingRule(payload);

      if (response.status === false || response.success === false) {
        handleApiErrors(response);
        return;
      }

      toast.success('Routing rule created successfully');
      handleCloseAddRuleModal();
      loadRoutingRules();
    } catch (error: any) {
      console.error('Error creating routing rule:', error);

      if (error.response?.data) {
        handleApiErrors(error.response.data);
      } else {
        toast.error(error?.message || 'Failed to create routing rule');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // const loadSettlementtats = async () => {
  //   try {
  //     const stats = await settlementService.getSettlementsStats();
  //     setSettlementtats(stats);
  //   } catch (error) {
  //     console.error('Error loading transaction stats:', error);
  //     // Keep default stats on error
  //   }
  // };

  // Client-side search filtering (only for search term)
  const filteredRoutingRule = RoutingRules.filter(txn => {
    if (!searchTerm) return true;
    return (
      txn.transaction_id?.toString().toLowerCase().includes(searchTerm.toLowerCase()) ||
      txn.merchant_transaction_id?.toString().toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  // Breadcrumbs
  const breadcrumbConfig = getBreadcrumbConfig('/Routing Rule');
  const breadcrumbs = useCommonBreadcrumbs({
    hideBreadcrumbs: breadcrumbConfig.hideBreadcrumbs,
    customBreadcrumbs: breadcrumbConfig.hideBreadcrumbs ? [] : undefined,
  });

  const finalBreadcrumbs =
    breadcrumbs.length > 0
      ? breadcrumbs
      : [
        {
          label: 'Dashboard',
          href: '/merchant/dashboard',
          onClick: () => navigate('/merchant/dashboard'),
        },
        {
          label: 'Routing Rule',
          active: true,
        },
      ];

  // Formatters
  const formatSettlementCurrency = (amount: number): string =>
    formatCurrency(amount, 'INR', 'en-IN');

  const formatSettlementDate = (dateString: string): string =>
    formatMomentDate(dateString, 'MMM DD, YYYY HH:mm');

  // Subtitle generator
  const getSubtitle = () => {
    const parts: string[] = [];

    if (statusFilter) {
      parts.push(`Status: ${STATUSES[statusFilter as keyof typeof STATUSES] || statusFilter}`);
    }

    if (searchTerm) {
      parts.push(`Search: "${searchTerm}"`);
    }

    const total = searchTerm ? filteredRoutingRule.length : totalCount;
    const base = 'Manage Routing Rule';

    return parts.length > 0
      ? `${base} • ${parts.join(' • ')} (${total} ${total === 1 ? 'transaction' : 'Routing Rule'})`
      : `${base} (${total} ${total === 1 ? 'transaction' : 'Routing Rule'})`;
  };

  // Sorting handlers - these will trigger API calls via useEffect
  const handleSort = (field: keyof any) => {
    setIsLoading(true);
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setPage(0); // Reset to first page when sorting
  };

  // Get sort icon for column
  const getSortIcon = (field: keyof any) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? (
      <ArrowUpwardIcon fontSize="small" />
    ) : (
      <ArrowDownwardIcon fontSize="small" />
    );
  };

  const handleStrategyChange =
    (index: number, field: string) =>
      (event: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
        const value = event.target.value;
        setFormData(prev => ({
          ...prev,
          strategies: prev.strategies.map((strategy, i) =>
            i === index
              ? { ...strategy, [field]: field === 'payment_gateway' ? value : Number(value) }
              : strategy
          ),
        }));
        // Clear error
        const errorKey = `strategies.${index}.${field}`;
        if (formErrors[errorKey]) {
          setFormErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[errorKey];
            return newErrors;
          });
        }
      };

  const handleAddStrategy = () => {
    setFormData(prev => ({
      ...prev,
      strategies: [
        ...prev.strategies,
        {
          payment_gateway: '',
          sequence_order: prev.strategies.length + 1,
          batch_size: 1,
          duration_seconds: 0,
          weight: 0,
        },
      ],
    }));
  };

  const handleRemoveStrategy = (index: number) => {
    if (formData.strategies.length > 1) {
      setFormData(prev => ({
        ...prev,
        strategies: prev.strategies
          .filter((_, i) => i !== index)
          .map((strategy, i) => ({
            ...strategy,
            sequence_order: i + 1,
          })),
      }));
      // Clear errors for removed strategy
      setFormErrors(prev => {
        const newErrors = { ...prev };
        Object.keys(newErrors).forEach(key => {
          if (key.startsWith(`strategies.${index}.`)) {
            delete newErrors[key];
          }
        });
        return newErrors;
      });
    }
  };

  // Table columns configuration
  const columns = [
    { field: 'name', headerName: 'Name', sortable: true, align: 'left' as const },
    { field: 'priority', headerName: 'Priority', sortable: true, align: 'left' as const },
    { field: 'strategy', headerName: 'Strategy', sortable: true, align: 'right' as const },
    { field: 'status', headerName: 'Status', sortable: true, align: 'right' as const },
    { field: 'attribute', headerName: 'Attribute', sortable: true, align: 'left' as const },
    { field: 'pg', headerName: 'PG', sortable: true, align: 'left' as const },
    { field: 'op', headerName: 'OP', sortable: true, align: 'center' as const },
    { field: 'value', headerName: 'Value', sortable: true, align: 'center' as const },
    { field: 'mode', headerName: 'Mode', sortable: true, align: 'center' as const },
    { field: 'create_at', headerName: 'Created at', sortable: true, align: 'left' as const },
    { field: 'update_at', headerName: 'Update at', sortable: true, align: 'left' as const },
  ];

  // Drawer handlers
  const handleOpenFilterDrawer = (transaction: any) => {
    setSelectedRoutingRules(transaction);
    setIsFilterDrawerOpen(true);
  };

  const handleCloseFilterDrawer = () => {
    setIsFilterDrawerOpen(false);
    setSelectedRoutingRules(null);
  };

  const handleDeleteClick = (rule: any) => {
    setRuleToDelete(rule);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!ruleToDelete) return;

    setIsDeleting(true);
    try {
      await routingRulesService.deleteRoutingRule(ruleToDelete.uuid);
      toast.success('Routing rule deleted successfully');
      loadRoutingRules();
      setIsDeleteDialogOpen(false);
      setRuleToDelete(null);
    } catch (error: any) {
      console.error('Error deleting routing rule:', error);
      toast.error(error?.message || 'Failed to delete routing rule');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setIsDeleteDialogOpen(false);
    setRuleToDelete(null);
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    // Both tabs call the same API for now
    loadRoutingRules();
  };

  return (
    <Box>
      <PageHeader
        title="Routing"
        //subtitle={getSubtitle()}
        //breadcrumbs={finalBreadcrumbs}
        actions={[
          {
            label: 'Add Routing Rule',
            icon: <AddIcon sx={{ fontSize: 16, color: '#fff' }} />,
            onClick: handleOpenAddRuleModal,
            variant: 'contained' as const,
            size: 'medium' as const,
          },
        ]}
        showDivider={false}
      />

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 2 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label="Active" />
          <Tab label="Under-Review" />
        </Tabs>
      </Box>

      {/* HTML Table with Pagination */}
      <Paper sx={{ width: '100%', overflow: 'hidden', mt: 2 }}>
        <TableContainer
          sx={{
            maxHeight: { xs: 400, sm: 600 },
            overflowX: 'auto',
            '&::-webkit-scrollbar': {
              height: 8,
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'rgba(0,0,0,0.1)',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0,0,0,0.3)',
              borderRadius: 4,
            },
          }}
        >
          <Table stickyHeader sx={{ minWidth: { xs: 800, sm: 'auto' } }}>
            <TableHead>
              <TableRow>
                {columns.map(column => (
                  <TableCell
                    key={column.field}
                    align={column.align}
                    sx={{
                      fontWeight: 'bold',
                      backgroundColor: '#f5f5f5',
                      cursor: column.sortable ? 'pointer' : 'default',
                      '&:hover': column.sortable ? { backgroundColor: '#e0e0e0' } : {},
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      padding: { xs: '8px 4px', sm: '16px' },
                      whiteSpace: 'nowrap',
                    }}
                  //onClick={() => column.sortable && handleSort(column.field as keyof any)}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {column.headerName}
                      {column.sortable && getSortIcon(column.field as keyof any)}
                    </Box>
                  </TableCell>
                ))}
                <TableCell
                  sx={{
                    fontWeight: 'bold',
                    backgroundColor: '#f5f5f5',
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    padding: { xs: '8px 4px', sm: '16px' },
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={columns.length + 1} align="center" sx={{ py: 4 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                      <CircularProgress size={24} sx={{ mr: 2 }} />
                      <Typography variant="body1" color="textSecondary">
                        Loading Routing Rules...
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : filteredRoutingRule.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length + 1} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="textSecondary">
                      No Routing Rules found matching your search criteria.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredRoutingRule.map(RoutingRules => (
                  <TableRow key={RoutingRules.settlement_id} hover>
                    <TableCell
                      sx={{
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        padding: { xs: '8px 4px', sm: '16px' },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography
                          variant="body2"
                          fontWeight="medium"
                          fontFamily="monospace"
                          sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                        >
                          {String(RoutingRules.name).charAt(0).toUpperCase() +
                            String(RoutingRules.name).slice(1)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        padding: { xs: '8px 4px', sm: '16px' },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>

                        <Typography
                          variant="body2"
                          fontFamily="monospace"
                          sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                        >
                          {String(RoutingRules.priority)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        padding: { xs: '8px 4px', sm: '16px' },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>

                        <Typography
                          variant="body2"
                          fontFamily="monospace"
                          sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                        >
                          {String(RoutingRules.strategy)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        padding: { xs: '8px 4px', sm: '16px' },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Tooltip title={RoutingRules.active ? 'Active' : 'Inactive'}>
                          <Chip
                            size="small"
                            label={RoutingRules.active ? 'Active' : 'Inactive'}
                            color={RoutingRules.active ? 'primary' : 'error'}
                            variant="outlined"
                            sx={{
                              fontWeight: 'normal',
                              fontSize: { xs: '0.625rem', sm: '0.75rem' },
                              borderRadius: '12px',
                              '& .MuiChip-label': { px: 1 },
                            }}
                          />
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        padding: { xs: '8px 4px', sm: '16px' },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>

                        <Typography
                          variant="body2"
                          sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                        >
                          {String(RoutingRules.condition.attribute).charAt(0).toUpperCase() +
                            String(RoutingRules.condition.attribute).slice(1)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        padding: { xs: '8px 4px', sm: '16px' },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>

                          <Typography
                            variant="body2"
                            sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                          >
                            {String(RoutingRules.payment_gateway_name ?? '-')}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        padding: { xs: '8px 4px', sm: '16px' },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>

                          <Typography
                            variant="body2"
                            sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                          >
                            {String(RoutingRules.condition.op ?? '-')}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        padding: { xs: '8px 4px', sm: '16px' },
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                      >
                        {String(RoutingRules.condition.value ?? '-')}
                      </Typography>
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        padding: { xs: '8px 4px', sm: '16px' },
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                      >
                        {String(RoutingRules.condition.mode ?? '-')}
                      </Typography>
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        padding: { xs: '8px 4px', sm: '16px' },
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                      >
                        {formatSettlementDate(String(RoutingRules.created_at))}
                      </Typography>
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        padding: { xs: '8px 4px', sm: '16px' },
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                      >
                        {formatSettlementDate(String(RoutingRules.updated_at))}
                      </Typography>
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        padding: { xs: '8px 4px', sm: '16px' },
                      }}
                    >
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleOpenFilterDrawer(RoutingRules.strategies)}
                          title="View Details"
                          sx={{ padding: { xs: '4px', sm: '8px' } }}
                        >
                          <ViewIcon
                            fontSize="small"
                            sx={{ fontSize: { xs: '16px', sm: '20px' } }}
                          />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteClick(RoutingRules)}
                          title="Delete Rule"
                          sx={{ padding: { xs: '4px', sm: '8px' } }}
                        >
                          <DeleteIcon
                            fontSize="small"
                            sx={{ fontSize: { xs: '16px', sm: '20px' } }}
                          />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {/* <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={searchTerm ? filteredSettlement.length : totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Rows per page:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`}
        /> */}
      </Paper>

      {/* Add Routing Rule Modal */}
      <Dialog
        open={isAddRuleModalOpen}
        onClose={handleCloseAddRuleModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" component="h2">
              Add Routing Rule
            </Typography>
            <IconButton onClick={handleCloseAddRuleModal} size="small" sx={{ ml: 2 }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          <Box sx={{ pt: 2 }}>
            {/* First Row: Rule Name, Priority, Strategy, Active */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              <Box sx={{ flex: '1 1 220px', minWidth: '100px' }}>
                <TextField
                  label="Rule Name"
                  value={formData.name}
                  onChange={handleInputChange('name')}
                  error={!!formErrors.name}
                  helperText={formErrors.name}
                  fullWidth
                  required
                  size="small"
                />
              </Box>
              <Box sx={{ flex: '0 1 300px', minWidth: '100px' }}>
                <TextField
                  label="Priority"
                  type="number"
                  value={formData.priority}
                  onChange={handleInputChange('priority')}
                  error={!!formErrors.priority}
                  helperText={formErrors.priority}
                  fullWidth
                  required
                  inputProps={{ min: 0 }}
                  size="small"
                />
              </Box>
              <Box sx={{ flex: '0 1 300px', minWidth: '100px' }}>
                <FormControl fullWidth required error={!!formErrors.strategy} size="small">
                  <InputLabel>Strategy</InputLabel>
                  <Select
                    value={formData.strategy}
                    onChange={handleSelectChange('strategy')}
                    label="Sr."
                  >
                    {STRATEGY_OPTIONS.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {formErrors.strategy && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                      {formErrors.strategy}
                    </Typography>
                  )}
                </FormControl>
              </Box>
            </Box>

            {['FIXED', 'SEQUENTIAL', 'TIME_SLICE', 'WEIGHTED'].includes(formData.strategy) && (
              <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                {/* <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
                  <TextField
                    label="Sticky TTL (seconds)"
                    type="number"
                    value={formData.sticky_ttl_sec}
                    onChange={handleInputChange('sticky_ttl_sec')}
                    fullWidth
                    inputProps={{ min: 0 }}
                    helperText="Time to live in seconds for sticky routing"
                    size="small"
                  />
                </Box> */}
                <Box sx={{ flex: '1 1 220px', minWidth: '100px' }}>
                  <FormControl
                    fullWidth
                    required
                    error={!!formErrors['condition.attribute']}
                    size="small"
                  >
                    <InputLabel>Condition</InputLabel>
                    <Select
                      value={formData.condition.attribute}
                      onChange={handleConditionSelectChange('attribute')}
                      label="Condition"
                    >
                      {ATTRIBUTE_OPTIONS.map(option => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {formErrors['condition.attribute'] && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                        {formErrors['condition.attribute']}
                      </Typography>
                    )}
                  </FormControl>
                </Box>
                {formData.condition.attribute === 'payment_mode' ? (
                  <Box sx={{ flex: '0 1 300px', minWidth: '100px' }}>
                    <FormControl fullWidth required error={!!formErrors['condition.mode']} size="small">
                      <InputLabel>Payment Mode</InputLabel>
                      <Select
                        value={formData.condition.mode}
                        onChange={handleConditionSelectChange('mode')}
                        label="Payment Mode"
                      >
                        {PAYMENT_MODE_OPTIONS.map(option => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {formErrors['condition.mode'] && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                          {formErrors['condition.mode']}
                        </Typography>
                      )}
                    </FormControl>
                  </Box>
                ) : (
                  <Box sx={{ flex: '0 1 300px', minWidth: '100px' }}>
                    <FormControl fullWidth required error={!!formErrors['condition.op']} size="small">
                      <InputLabel>OP.</InputLabel>
                      <Select
                        value={formData.condition.op}
                        onChange={handleConditionSelectChange('op')}
                        label="OP."
                      >
                        {OPERATOR_OPTIONS.filter(option =>
                          formData.condition.attribute === 'failure'
                            ? option.value === 'GTE'
                            : true
                        ).map(option => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {formErrors['condition.op'] && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                          {formErrors['condition.op']}
                        </Typography>
                      )}
                    </FormControl>
                  </Box>
                )}
                {formData.condition.attribute !== 'payment_mode' && (
                  <Box sx={{ flex: '0 1 300px', minWidth: '100px' }}>
                    <TextField
                      label="Value"
                      value={formData.condition.value}
                      onChange={handleConditionChange('value')}
                      error={!!formErrors['condition.value']}
                      helperText={formErrors['condition.value']}
                      fullWidth
                      required
                      size="small"
                    />
                  </Box>
                )}
              </Box>
            )}

            {formData.strategy === 'FIXED' && (
              <Box sx={{ mb: 2 }}>
                {/* <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Payment Gateways
                  </Typography>
                  <Button
                    startIcon={<AddIcon />}
                    onClick={handleAddStrategy}
                    size="small"
                    variant="outlined"
                    sx={{ minWidth: 'auto', px: 1.5 }}
                  >
                    Add
                  </Button>
                </Box> */}

                {formData.strategies.map((strategy, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      gap: 2,
                      mb: 2,
                      alignItems: 'flex-start',
                      p: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      position: 'relative',
                    }}
                  >
                    <Box sx={{ flex: '1 1 220px', minWidth: '100px' }}>
                      <FormControl
                        fullWidth
                        required
                        error={!!formErrors[`strategies.${index}.payment_gateway`]}
                        size="small"
                      >
                        <InputLabel>Payment Gateway</InputLabel>
                        <Select
                          value={strategy.payment_gateway_uuid}
                          onChange={handleStrategyChange(index, 'payment_gateway')}
                          label="Payment Gateway"
                          disabled={isLoadingGateways}
                        >
                          {isLoadingGateways ? (
                            <MenuItem disabled>
                              <CircularProgress size={20} sx={{ mr: 1 }} />
                              Loading gateways...
                            </MenuItem>
                          ) : paymentGateways.length === 0 ? (
                            <MenuItem disabled>No payment gateways available</MenuItem>
                          ) : (
                            paymentGateways.map((gateway: any) => (
                              <MenuItem
                                key={gateway.payment_gateway_uuid}
                                value={gateway.payment_gateway_uuid}
                              >
                                {gateway.payment_gateway_label}
                              </MenuItem>
                            ))
                          )}
                        </Select>
                        {formErrors[`strategies.${index}.payment_gateway`] && (
                          <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                            {formErrors[`strategies.${index}.payment_gateway`]}
                          </Typography>
                        )}
                      </FormControl>
                    </Box>
                  </Box>
                ))}

                {formErrors.strategies && formData.strategies.length === 0 && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                    {formErrors.strategies}
                  </Typography>
                )}
              </Box>
            )}

            {formData.strategy === 'SEQUENTIAL' && (
              <Box sx={{ mb: 2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 2,
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Payment Gateways
                  </Typography>
                  <Button
                    startIcon={<AddIcon />}
                    onClick={handleAddStrategy}
                    size="small"
                    variant="outlined"
                    sx={{ minWidth: 'auto', px: 1.5 }}
                  >
                    Add
                  </Button>
                </Box>

                {formData.strategies.map((strategy, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      gap: 2,
                      mb: 2,
                      alignItems: 'flex-start',
                      p: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      position: 'relative',
                    }}
                  >
                    <Box sx={{ flex: '1 1 220px', minWidth: '100px' }}>
                      <FormControl
                        fullWidth
                        required
                        error={!!formErrors[`strategies.${index}.payment_gateway`]}
                        size="small"
                      >
                        <InputLabel>Payment Gateway</InputLabel>
                        <Select
                          value={strategy.payment_gateway_uuid}
                          onChange={handleStrategyChange(index, 'payment_gateway')}
                          label="Payment Gateway"
                          disabled={isLoadingGateways}
                        >
                          {isLoadingGateways ? (
                            <MenuItem disabled>
                              <CircularProgress size={20} sx={{ mr: 1 }} />
                              Loading gateways...
                            </MenuItem>
                          ) : paymentGateways.length === 0 ? (
                            <MenuItem disabled>No payment gateways available</MenuItem>
                          ) : (
                            paymentGateways.map((gateway: any) => (
                              <MenuItem
                                key={gateway.payment_gateway_uuid}
                                value={gateway.payment_gateway_uuid}
                              >
                                {gateway.payment_gateway_label}
                              </MenuItem>
                            ))
                          )}
                        </Select>
                        {formErrors[`strategies.${index}.payment_gateway`] && (
                          <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                            {formErrors[`strategies.${index}.payment_gateway`]}
                          </Typography>
                        )}
                      </FormControl>
                    </Box>

                    <Box sx={{ flex: '0 1 150px', minWidth: '100px' }}>
                      <TextField
                        label="Sequence"
                        type="number"
                        value={strategy.sequence_order}
                        onChange={handleStrategyChange(index, 'sequence_order')}
                        error={!!formErrors[`strategies.${index}.sequence_order`]}
                        helperText={formErrors[`strategies.${index}.sequence_order`]}
                        fullWidth
                        required
                        inputProps={{ min: 1 }}
                        size="small"
                      />
                    </Box>

                    <Box sx={{ flex: '0 1 150px', minWidth: '100px' }}>
                      <TextField
                        label="Batch Size"
                        type="number"
                        value={strategy.batch_size}
                        onChange={handleStrategyChange(index, 'batch_size')}
                        error={!!formErrors[`strategies.${index}.batch_size`]}
                        helperText={formErrors[`strategies.${index}.batch_size`]}
                        fullWidth
                        required
                        inputProps={{ min: 1 }}
                        size="small"
                      />
                    </Box>

                    <IconButton
                      onClick={() => handleRemoveStrategy(index)}
                      disabled={formData.strategies.length === 1}
                      color="error"
                      size="small"
                      sx={{
                        mt: 0.5,
                        minWidth: 'auto',
                        width: 32,
                        height: 32,
                      }}
                    >
                      <RemoveIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}

                {formErrors.strategies && formData.strategies.length === 0 && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                    {formErrors.strategies}
                  </Typography>
                )}
              </Box>
            )}

            {formData.strategy === 'TIME_SLICE' && (
              <Box sx={{ mb: 2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 2,
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Payment Gateways
                  </Typography>
                  <Button
                    startIcon={<AddIcon />}
                    onClick={handleAddStrategy}
                    size="small"
                    variant="outlined"
                    sx={{ minWidth: 'auto', px: 1.5 }}
                  >
                    Add
                  </Button>
                </Box>

                {formData.strategies.map((strategy, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      gap: 2,
                      mb: 2,
                      alignItems: 'flex-start',
                      p: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      position: 'relative',
                    }}
                  >
                    <Box sx={{ flex: '1 1 220px', minWidth: '100px' }}>
                      <FormControl
                        fullWidth
                        required
                        error={!!formErrors[`strategies.${index}.payment_gateway`]}
                        size="small"
                      >
                        <InputLabel>Payment Gateway</InputLabel>
                        <Select
                          value={strategy.payment_gateway_uuid}
                          onChange={handleStrategyChange(index, 'payment_gateway')}
                          label="Payment Gateway"
                          disabled={isLoadingGateways}
                        >
                          {isLoadingGateways ? (
                            <MenuItem disabled>
                              <CircularProgress size={20} sx={{ mr: 1 }} />
                              Loading gateways...
                            </MenuItem>
                          ) : paymentGateways.length === 0 ? (
                            <MenuItem disabled>No payment gateways available</MenuItem>
                          ) : (
                            paymentGateways.map((gateway: any) => (
                              <MenuItem
                                key={gateway.payment_gateway_uuid}
                                value={gateway.payment_gateway_uuid}
                              >
                                {gateway.payment_gateway_label}
                              </MenuItem>
                            ))
                          )}
                        </Select>
                        {formErrors[`strategies.${index}.payment_gateway`] && (
                          <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                            {formErrors[`strategies.${index}.payment_gateway`]}
                          </Typography>
                        )}
                      </FormControl>
                    </Box>

                    <Box sx={{ flex: '0 1 150px', minWidth: '100px' }}>
                      <TextField
                        label="Sequence"
                        type="number"
                        value={strategy.sequence_order}
                        onChange={handleStrategyChange(index, 'sequence_order')}
                        error={!!formErrors[`strategies.${index}.sequence_order`]}
                        helperText={formErrors[`strategies.${index}.sequence_order`]}
                        fullWidth
                        required
                        inputProps={{ min: 1 }}
                        size="small"
                      />
                    </Box>

                    <Box sx={{ flex: '0 1 150px', minWidth: '100px' }}>
                      <TextField
                        label="Duration Seconds"
                        type="number"
                        value={strategy.duration_seconds}
                        onChange={handleStrategyChange(index, 'duration_seconds')}
                        error={!!formErrors[`strategies.${index}.duration_seconds`]}
                        helperText={formErrors[`strategies.${index}.duration_seconds`]}
                        fullWidth
                        required
                        inputProps={{ min: 1 }}
                        size="small"
                      />
                    </Box>

                    <IconButton
                      onClick={() => handleRemoveStrategy(index)}
                      disabled={formData.strategies.length === 1}
                      color="error"
                      size="small"
                      sx={{
                        mt: 0.5,
                        minWidth: 'auto',
                        width: 32,
                        height: 32,
                      }}
                    >
                      <RemoveIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}

                {formErrors.strategies && formData.strategies.length === 0 && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                    {formErrors.strategies}
                  </Typography>
                )}
              </Box>
            )}

            {formData.strategy === 'WEIGHTED' && (
              <Box sx={{ mb: 2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 2,
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Payment Gateways
                  </Typography>
                  <Button
                    startIcon={<AddIcon />}
                    onClick={handleAddStrategy}
                    size="small"
                    variant="outlined"
                    sx={{ minWidth: 'auto', px: 1.5 }}
                  >
                    Add
                  </Button>
                </Box>

                {formData.strategies.map((strategy, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      gap: 2,
                      mb: 2,
                      alignItems: 'flex-start',
                      p: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      position: 'relative',
                    }}
                  >
                    <Box sx={{ flex: '1 1 220px', minWidth: '100px' }}>
                      <FormControl
                        fullWidth
                        required
                        error={!!formErrors[`strategies.${index}.payment_gateway`]}
                        size="small"
                      >
                        <InputLabel>Payment Gateway</InputLabel>
                        <Select
                          value={strategy.payment_gateway_uuid}
                          onChange={handleStrategyChange(index, 'payment_gateway')}
                          label="Payment Gateway"
                          disabled={isLoadingGateways}
                        >
                          {isLoadingGateways ? (
                            <MenuItem disabled>
                              <CircularProgress size={20} sx={{ mr: 1 }} />
                              Loading gateways...
                            </MenuItem>
                          ) : paymentGateways.length === 0 ? (
                            <MenuItem disabled>No payment gateways available</MenuItem>
                          ) : (
                            paymentGateways.map((gateway: any) => (
                              <MenuItem
                                key={gateway.payment_gateway_uuid}
                                value={gateway.payment_gateway_uuid}
                              >
                                {gateway.payment_gateway_label}
                              </MenuItem>
                            ))
                          )}
                        </Select>
                        {formErrors[`strategies.${index}.payment_gateway`] && (
                          <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                            {formErrors[`strategies.${index}.payment_gateway`]}
                          </Typography>
                        )}
                      </FormControl>
                    </Box>

                    <Box sx={{ flex: '0 1 150px', minWidth: '100px' }}>
                      <TextField
                        label="Sequence"
                        type="number"
                        value={strategy.sequence_order}
                        onChange={handleStrategyChange(index, 'sequence_order')}
                        error={!!formErrors[`strategies.${index}.sequence_order`]}
                        helperText={formErrors[`strategies.${index}.sequence_order`]}
                        fullWidth
                        required
                        inputProps={{ min: 1 }}
                        size="small"
                      />
                    </Box>

                    <Box sx={{ flex: '0 1 150px', minWidth: '100px' }}>
                      <TextField
                        label="Weight"
                        type="number"
                        value={strategy.weight}
                        onChange={handleStrategyChange(index, 'weight')}
                        error={!!formErrors[`strategies.${index}.weight`]}
                        helperText={formErrors[`strategies.${index}.weight`]}
                        fullWidth
                        required
                        inputProps={{ min: 1 }}
                        size="small"
                      />
                    </Box>

                    <IconButton
                      onClick={() => handleRemoveStrategy(index)}
                      disabled={formData.strategies.length === 1}
                      color="error"
                      size="small"
                      sx={{
                        mt: 0.5,
                        minWidth: 'auto',
                        width: 32,
                        height: 32,
                      }}
                    >
                      <RemoveIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}

                {formErrors.strategies && formData.strategies.length === 0 && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                    {formErrors.strategies}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseAddRuleModal} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
          >
            {isSubmitting ? 'Creating...' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Transaction Details Drawer */}
      <Drawer
        anchor="right"
        open={isFilterDrawerOpen}
        onClose={handleCloseFilterDrawer}
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '100%', sm: 900 },
            padding: { xs: 1, sm: 2 },
            pt: { xs: 8, sm: 10 },
          },
        }}
      >
        {selectedRoutingRules ? (
          <Box display="flex" flexDirection="column" gap={2}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'right' }}>
              <label onClick={handleCloseFilterDrawer} style={{ cursor: 'pointer' }}>
                <CloseIcon style={{ color: '#17A6DA', fontSize: '30px' }} />
              </label>
            </Box>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell colSpan={9} sx={{ fontWeight: 700 }}>
                      Rules
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>uuid</TableCell>
                    <TableCell>Payment Gateway Name</TableCell>
                    <TableCell>Sequence Order</TableCell>
                    <TableCell>Batch Size</TableCell>
                    <TableCell>Duration Seconds</TableCell>
                    <TableCell>Weight</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Array.isArray(selectedRoutingRules) && selectedRoutingRules.length > 0 ? (
                    selectedRoutingRules.map((item: any, index: number) => (
                      <TableRow>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{item.uuid ?? '-'}</TableCell>
                        <TableCell>{item.payment_gateway_name ?? '-'}</TableCell>
                        <TableCell>{item.sequence_order ?? '-'}</TableCell>
                        <TableCell>{item.batch_size ?? '-'}</TableCell>
                        <TableCell>{item.duration_seconds ?? '-'}</TableCell>
                        <TableCell>{item.weight ?? '-'}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography variant="body2" color="textSecondary">
                          No rules available.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ) : (
          <Box display="flex" flexDirection="column" gap={2}>
            <Typography variant="body2" color="textSecondary">
              Select a rule to view its details.
            </Typography>
          </Box>
        )}
      </Drawer>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={isDeleteDialogOpen}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6">Delete Routing Rule</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to delete this routing rule?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleDeleteCancel} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="primary"
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={20} /> : ''}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RoutingRule;
